import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Papa from "papaparse";
import { Upload, Trash2, Undo2 } from "lucide-react";

export const GrantsImporter = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [csvData, setCsvData] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setCsvData(content);
      };
      reader.readAsText(selectedFile);
    }
  };

  const parseCsvData = (csvText: string) => {
    const result = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (h) => h.trim().toLowerCase(),
    });

    if (result.errors && result.errors.length) {
      console.warn('CSV parse errors:', result.errors.slice(0, 3));
    }

    const rows = (result.data as any[]).filter(Boolean);

    const normalized = rows.map((row) => {
      const get = (...candidates: string[]) => {
        for (const key of Object.keys(row)) {
          const k = key.trim().toLowerCase();
          if (candidates.some((c) => k.includes(c))) {
            return (row[key] ?? '').toString().trim();
          }
        }
        return '';
      };

      const eligibleRaw = get('eligible_use_buckets', 'uses', 'categories');
      const eligible = eligibleRaw
        ? eligibleRaw
            .split(/\s*[;|]\s*|\s*\/\s*|\s*\|\s*|\s*,\s*/)
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      const deadline = get('deadline');

      const record: any = {
        funder_program: get('funder_program', 'funder', 'program_name', 'program'),
        program_focus: get('program_focus', 'focus'),
        typical_grant_size: get('typical_grant_size', 'size'),
        cycle_timeline: get('cycle', 'timeline'),
        geography: get('geography', 'region', 'state'),
        eligible_use_buckets: eligible,
        contact_name: get('contact_name', 'contact'),
        contact_email: get('contact_email', 'email'),
        phone: get('phone'),
        application_info_url: get('application_info_url', 'url', 'link'),
        notes: get('notes', 'note'),
        eligibility_type: get('eligibility'),
        deadline_date: deadline || null,
      };

      if (!record.funder_program) return null;
      return record;
    }).filter(Boolean);

    return normalized as any[];
  };

  const handleImport = async () => {
    if (!csvData.trim()) {
      toast({ title: "Error", description: "Please paste CSV data or select a file first.", variant: "destructive" });
      return;
    }

    setIsImporting(true);
    try {
      const parsed = parseCsvData(csvData);
      if (parsed.length === 0) {
        toast({ title: "Error", description: "No valid data found in CSV.", variant: "destructive" });
        return;
      }

      const { data, error } = await supabase.functions.invoke('import-grants', { body: { data: parsed } });
      if (error) throw error;

      toast({ title: "Success", description: data?.message || `Imported ${parsed.length} grants.` });
      setCsvData("");
      setFile(null);
    } catch (err: any) {
      console.error('Grants import error:', err);
      toast({ title: "Error", description: `Failed to import: ${err.message || err}`, variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  const handleClearData = async () => {
    if (!confirm("Delete ALL grants? This cannot be undone.")) return;
    setIsClearing(true);
    try {
      const { data, error } = await supabase.functions.invoke('import-grants', { method: 'DELETE', body: { action: 'clear_all' } });
      if (error) throw error;
      toast({ title: "Success", description: data?.message || "All grants cleared." });
    } catch (err: any) {
      console.error('Grants clear error:', err);
      toast({ title: "Error", description: `Failed to clear: ${err.message || err}`, variant: "destructive" });
    } finally {
      setIsClearing(false);
    }
  };

  const handleUndoLastImport = async () => {
    setIsUndoing(true);
    try {
      const { data, error } = await supabase.functions.invoke('import-grants', { method: 'DELETE', body: { action: 'undo_last' } });
      if (error) throw error;
      toast({ title: "Success", description: data?.message || "Last import undone." });
    } catch (err: any) {
      console.error('Grants undo error:', err);
      toast({ title: "Error", description: `Failed to undo: ${err.message || err}`, variant: "destructive" });
    } finally {
      setIsUndoing(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" /> Grants Importer
        </CardTitle>
        <CardDescription>
          Upload a CSV with columns: funder_program, program_focus, typical_grant_size, cycle_timeline, geography, eligible_use_buckets, contact_name, contact_email, phone, application_info_url, notes, eligibility_type, deadline_date
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Upload CSV File</label>
          <Input type="file" accept=".csv,.txt" onChange={handleFileChange} className="mb-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Or Paste CSV Data</label>
          <Textarea
            placeholder="Paste CSV here..."
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            rows={10}
            className="font-mono text-sm"
          />
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleImport} disabled={isImporting || !csvData.trim()} className="flex-1 min-w-32">
            {isImporting ? "Importing..." : "Import Grants"}
          </Button>
          <Button onClick={handleUndoLastImport} disabled={isUndoing} variant="secondary" className="flex items-center gap-2">
            <Undo2 className="h-4 w-4" />
            {isUndoing ? "Undoing..." : "Undo Last Import"}
          </Button>
          <Button onClick={handleClearData} disabled={isClearing} variant="destructive" className="flex items-center gap-2">
            <Trash2 className="h-4 w-4" />
            {isClearing ? "Clearing..." : "Clear All"}
          </Button>
        </div>

        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <strong>CSV Example:</strong><br />
          <div className="mt-2">
            <code>
              funder_program,program_focus,typical_grant_size,cycle_timeline,geography,eligible_use_buckets,contact_name,contact_email,phone,application_info_url,notes,eligibility_type,deadline_date
            </code>
            <br />
            <code>
              USDA Beginning Farmer Program,Education & TA,$50k-$750k,Annual,United States,"Education;Training;Mentorship",Sarah Johnson,sarah@usda.gov,202-555-0123,https://example.com,Priority for new farmers,Beginning Farmers,2025-02-15
            </code>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};